/**
 * AICartConfigCard — admin editor for every AI Cart module knob.
 *
 * Data source: `/api/admin/ai-cart-config` (GET/PUT/reset).
 *
 * Five sections, each self-contained so admins can jump straight to the
 * knob they care about:
 *   1. VIP tiers            — Bronze/Silver/Gold thresholds + discount %
 *   2. Cheer Wall           — on/off + retention window
 *   3. Credits              — cost per URL ingest + low-balance warning
 *   4. Marketplaces         — enable/disable each parser
 *   5. Share links          — default TTL + max TTL
 *
 * A single "Save" button PUTs the whole config atomically. Every input
 * flips the local `dirty` flag so admins can see they've made changes,
 * and a Reset button restores shipped defaults.
 */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, RotateCcw, Award, Megaphone, Coins, Store, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";

interface Tier {
  key: string;
  label: string;
  min_reactions: number;
  discount_pct: number;
  medal?: string | null;
  color?: string | null;
}
interface CheerWall { enabled: boolean; retention_days: number; }
interface Credits { cost_per_ingest: number; min_balance_warn: number; }
interface Marketplace { key: string; label: string; enabled: boolean; }
interface Share { default_ttl_days: number; max_ttl_days: number; }

interface AICartConfig {
  tiers: Tier[];
  cheer_wall: CheerWall;
  credits: Credits;
  marketplaces: Marketplace[];
  share: Share;
}

export const AICartConfigCard: React.FC = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const readonly = (me?.role || "user") !== "admin";

  const q = useQuery<AICartConfig>({
    queryKey: ["admin-ai-cart-config"],
    queryFn: async () => (await api.get("/admin/ai-cart-config")).data,
  });

  const [draft, setDraft] = React.useState<AICartConfig | null>(null);
  const [dirty, setDirty] = React.useState(false);
  React.useEffect(() => { if (q.data && !draft) setDraft(q.data); }, [q.data, draft]);

  const patch = (path: (cfg: AICartConfig) => AICartConfig) => {
    if (!draft) return;
    setDraft(path(structuredClone(draft)));
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: async () => (await api.put("/admin/ai-cart-config", draft)).data,
    onSuccess: (data) => {
      setDraft(data);
      setDirty(false);
      toast.success("AI Cart™ settings saved.");
      qc.invalidateQueries({ queryKey: ["admin-ai-cart-config"] });
      qc.invalidateQueries({ queryKey: ["ai-cart-config"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const reset = useMutation({
    mutationFn: async () => (await api.post("/admin/ai-cart-config/reset")).data,
    onSuccess: (data) => {
      setDraft(data);
      setDirty(false);
      toast.success("Restored shipped defaults.");
      qc.invalidateQueries({ queryKey: ["admin-ai-cart-config"] });
    },
    onError: () => toast.error("Reset failed"),
  });

  if (q.isLoading || !draft) {
    return <div className="sg-card p-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading AI Cart™ configuration…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-ai-cart-config">
      {/* Header + save controls */}
      <div className="sg-card p-5 flex items-center gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-lg grid place-items-center bg-gradient-to-br from-orange-500 to-fuchsia-600 text-white shrink-0">
          <Award className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-lg">AI Cart™ Configuration</div>
          <div className="text-xs text-slate-500">
            Every knob for the /ai-cart module in one place. Changes go live immediately for every shopper.
          </div>
        </div>
        <Button
          onClick={() => reset.mutate()}
          disabled={readonly || reset.isPending}
          variant="outline"
          data-testid="ai-cart-config-reset-btn"
          size="sm"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset to defaults
        </Button>
        <Button
          onClick={() => save.mutate()}
          disabled={readonly || !dirty || save.isPending}
          data-testid="ai-cart-config-save-btn"
          size="sm"
        >
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
          {dirty ? "Save changes" : "Saved"}
        </Button>
      </div>

      {/* 1. VIP Tiers */}
      <section className="sg-card p-5" data-testid="ai-cart-config-tiers">
        <SectionHeader
          icon={<Award className="h-4 w-4 text-amber-600" />}
          title="VIP loyalty tiers"
          subtitle="Reaction cutoffs and discount % for each shopper tier."
        />
        <div className="mt-4 grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center text-xs font-semibold text-slate-500 uppercase tracking-widest">
          <div>Tier</div><div>Min reactions</div><div>Discount %</div><div>Medal</div>
        </div>
        {draft.tiers.map((t, i) => (
          <div key={t.key} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center py-2 border-t border-slate-100 dark:border-slate-800">
            <div className="text-sm font-semibold flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: t.color || "#94a3b8" }} />
              {t.label}
            </div>
            <NumberInput
              value={t.min_reactions}
              onChange={(v) => patch((d) => { d.tiers[i].min_reactions = v; return d; })}
              min={0} max={1000}
              testId={`ai-cart-config-tier-min-${t.key}`}
              disabled={readonly}
            />
            <NumberInput
              value={t.discount_pct}
              onChange={(v) => patch((d) => { d.tiers[i].discount_pct = v; return d; })}
              min={0} max={100}
              suffix="%"
              testId={`ai-cart-config-tier-pct-${t.key}`}
              disabled={readonly}
            />
            <input
              value={t.medal || ""}
              onChange={(e) => patch((d) => { d.tiers[i].medal = e.target.value; return d; })}
              className="w-14 text-center text-lg py-1 border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-950"
              maxLength={4}
              disabled={readonly}
              data-testid={`ai-cart-config-tier-medal-${t.key}`}
            />
          </div>
        ))}
      </section>

      {/* 2. Cheer Wall */}
      <section className="sg-card p-5" data-testid="ai-cart-config-cheer-wall">
        <SectionHeader
          icon={<Megaphone className="h-4 w-4 text-fuchsia-600" />}
          title="Cheer Wall"
          subtitle="Public ticker of recent tier upgrades on the AI Cart™ page."
        />
        <div className="mt-4 flex items-center justify-between gap-4">
          <div className="text-sm">
            <div className="font-semibold">Show the Cheer Wall to shoppers</div>
            <div className="text-xs text-slate-500">Turn off during maintenance or launches.</div>
          </div>
          <Switch
            checked={draft.cheer_wall.enabled}
            onCheckedChange={(v: boolean) => patch((d) => { d.cheer_wall.enabled = v; return d; })}
            data-testid="ai-cart-config-cheer-toggle"
            disabled={readonly}
          />
        </div>
        <div className="mt-4 flex items-center gap-4">
          <div className="text-sm flex-1">
            <div className="font-semibold">Retention window (days)</div>
            <div className="text-xs text-slate-500">How far back tier upgrades stay visible on the wall.</div>
          </div>
          <NumberInput
            value={draft.cheer_wall.retention_days}
            onChange={(v) => patch((d) => { d.cheer_wall.retention_days = v; return d; })}
            min={1} max={365}
            testId="ai-cart-config-cheer-retention"
            disabled={readonly}
          />
        </div>
      </section>

      {/* 3. Credits */}
      <section className="sg-card p-5" data-testid="ai-cart-config-credits">
        <SectionHeader
          icon={<Coins className="h-4 w-4 text-emerald-600" />}
          title="Credits & ingest cost"
          subtitle="Workspace credits consumed when a shopper analyses a new URL."
        />
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <NumberField
            label="Cost per URL ingest"
            hint="Deducted from the shopper's workspace credits per analyze."
            value={draft.credits.cost_per_ingest}
            onChange={(v) => patch((d) => { d.credits.cost_per_ingest = v; return d; })}
            min={0} max={100}
            testId="ai-cart-config-credit-cost"
            disabled={readonly}
          />
          <NumberField
            label="Low-balance warning at"
            hint="Show a friendly top-up nudge when remaining credits fall below this."
            value={draft.credits.min_balance_warn}
            onChange={(v) => patch((d) => { d.credits.min_balance_warn = v; return d; })}
            min={0} max={1000}
            testId="ai-cart-config-credit-warn"
            disabled={readonly}
          />
        </div>
      </section>

      {/* 4. Marketplaces */}
      <section className="sg-card p-5" data-testid="ai-cart-config-marketplaces">
        <SectionHeader
          icon={<Store className="h-4 w-4 text-sky-600" />}
          title="Marketplace whitelist"
          subtitle="Which parsers accept incoming URLs. Off = shoppers see a friendly 'unsupported' toast."
        />
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {draft.marketplaces.map((m, i) => (
            <label
              key={m.key}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                m.enabled
                  ? "border-emerald-400 bg-emerald-50/60 dark:bg-emerald-950/20"
                  : "border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/50 opacity-70"
              }`}
              data-testid={`ai-cart-config-marketplace-${m.key}`}
            >
              <input
                type="checkbox"
                checked={m.enabled}
                onChange={(e) => patch((d) => { d.marketplaces[i].enabled = e.target.checked; return d; })}
                className="accent-emerald-500"
                disabled={readonly}
              />
              <span className="text-sm font-medium">{m.label}</span>
            </label>
          ))}
        </div>
      </section>

      {/* 5. Share links */}
      <section className="sg-card p-5" data-testid="ai-cart-config-share">
        <SectionHeader
          icon={<Link2 className="h-4 w-4 text-violet-600" />}
          title="Share links"
          subtitle="Default expiry for AI Cart™ share links your shoppers create."
        />
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <NumberField
            label="Default TTL (days)"
            hint="Fresh share links expire after this many days."
            value={draft.share.default_ttl_days}
            onChange={(v) => patch((d) => { d.share.default_ttl_days = v; return d; })}
            min={1} max={365}
            testId="ai-cart-config-share-default-ttl"
            disabled={readonly}
          />
          <NumberField
            label="Max TTL (days)"
            hint="Ceiling — shoppers cannot pick a longer expiry than this."
            value={draft.share.max_ttl_days}
            onChange={(v) => patch((d) => { d.share.max_ttl_days = v; return d; })}
            min={draft.share.default_ttl_days} max={365}
            testId="ai-cart-config-share-max-ttl"
            disabled={readonly}
          />
        </div>
      </section>
    </div>
  );
};

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle: string }> = ({ icon, title, subtitle }) => (
  <div className="flex items-start gap-2">
    <div className="mt-0.5 shrink-0">{icon}</div>
    <div>
      <div className="font-display font-bold text-base">{title}</div>
      <div className="text-xs text-slate-500">{subtitle}</div>
    </div>
  </div>
);

const NumberInput: React.FC<{
  value: number; onChange: (v: number) => void; min?: number; max?: number; suffix?: string; testId?: string; disabled?: boolean;
}> = ({ value, onChange, min, max, suffix, testId, disabled }) => (
  <div className="inline-flex items-center rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 overflow-hidden">
    <input
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value) || 0)}
      className="w-16 py-1 px-2 text-sm bg-transparent outline-none tabular-nums text-right"
      data-testid={testId}
      disabled={disabled}
    />
    {suffix && <span className="pr-2 text-xs text-slate-500">{suffix}</span>}
  </div>
);

const NumberField: React.FC<{
  label: string; hint?: string; value: number; onChange: (v: number) => void; min?: number; max?: number; testId?: string; disabled?: boolean;
}> = ({ label, hint, value, onChange, min, max, testId, disabled }) => (
  <div className="flex items-center gap-4">
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold">{label}</div>
      {hint && <div className="text-xs text-slate-500">{hint}</div>}
    </div>
    <NumberInput value={value} onChange={onChange} min={min} max={max} testId={testId} disabled={disabled} />
  </div>
);

export default AICartConfigCard;
